<template>
    <div class="wizard">
        <div class="wizard-header d-flex justify-content-between align-items-center">
            <h2>Add Verified Caller ID</h2>
            <p class="text-blue cursor-pointer"
               @click="dialogWhatIsVerifiedCallerId = true">
                What is a Verified Caller ID?
            </p>
        </div>
        <div class="dropdown-divider"></div>
        <div :class="{'calling' : pin}">
            <div class="row">
                <div class="col-12">
                    <div v-if="pin">
                        <p class="text-center text-lg mt-4">
                            We are calling the following number: <strong>{{ fixedPhone }}</strong><br/><br>
                            Please answer and then enter the PIN code on your mobile device.
                        </p>
                        <p class="text-3x text-yellow text-monospace font-weight-bold text-separator text-center">
                            <span class="pin-code p-2 text-center">{{ pin }}</span>
                        </p>
                    </div>
                    <el-form ref="add-new-caller-id"
                             label-position="top"
                             v-loading="loading"
                             @submit.prevent.native="handleEnterKey"
                             v-else>
                        <el-form-item class="mb-4"
                                      label="Country">
                            <country-selector :show_prefix="true"
                                              :force_initial_country_code="true"
                                              :show_tips="true"
                                              :disabled="loading"
                                              @changed="changeCountry">
                            </country-selector>
                        </el-form-item>
                        
                        <div class="row">
                            <div class="col-md-8">
                                <el-form-item label="Phone number">
                                    <el-input type="text"
                                              v-model="phoneNumber"
                                              :disabled="loading"
                                              placeholder="Enter a phone number"
                                              clearable
                                              maxlength="15"
                                              @keydown.enter.native="handleEnterKey">
                                    </el-input>
                                </el-form-item>
                            </div>
                            <div class="col-md-4">
                                <el-form-item label="Extension (optional)">
                                    <el-input type="text"
                                              v-model="extension"
                                              :disabled="loading"
                                              placeholder="Ext"
                                              clearable
                                              maxlength="10"
                                              @keydown.enter.native="handleEnterKey">
                                    </el-input>
                                </el-form-item>
                            </div>
                        </div>
                        
                        <div class="text-right mt-3">
                            <el-button type="success"
                                       v-if="!pin"
                                       @click="handleButtonClick"
                                       :disabled="sendButtonDisabled">
                                Verify number
                            </el-button>
                        </div>
                    </el-form>
                </div>
            </div>
        </div>
        <el-dialog top="10vh"
                   title="What is a Verified Caller ID?"
                   :width="'550px'"
                   :visible.sync="dialogWhatIsVerifiedCallerId"
                   append-to-body>
            <p class="wz-text-wrap">
                A Verified Caller ID is an existing non-Aloware phone number (like your personal mobile number or office landline) that you validate through a one-time passcode (OTP). Once verified, the number can be used as the caller ID for outbound calls.
                <br/><br/>
                <strong>Usage Limitations</strong>
                <br/><br/>
                Verified Caller IDs cannot be used as the display name on outgoing SMS or MMS messages.
                <br/><br/>
                They are intended for voice services only.
                <br/><br/>
                While you can make outbound calls using a verified number, you cannot receive inbound calls into Aloware on that number. Incoming calls will still route through your existing service provider (wireless carrier, landline, etc.).
                <br/><br/>
                Explore our <a class="text-blue font-weight-bold" href="https://aloware.navattic.com/3l9o012m" target="_blank">interactive demo for a step-by-step walkthrough on adding a verified caller ID</a>, or visit our <a class="text-blue font-weight-bold" href="https://support.aloware.com/en/articles/9031270-lines-general-settings" target="_blank">site</a> to learn more.
            </p>
            <span slot="footer"
                  class="dialog-footer">
                <button class="btn"
                        @click.prevent="dialogWhatIsVerifiedCallerId = false">
                    OK
                </button>
            </span>
        </el-dialog>
    </div>
</template>

<style scoped>
.pin-code {
    letter-spacing: 20px;
    color: #a2ffc9;
    text-align: center;
    background-color: #606266;
    border-radius: 20px !important;
    padding-left: 28px !important;
}

.calling {
    background-size: 320px;
    background-position-y: -40px;
    padding-top: 120px;
}
</style>

<script>
import {form_validation_mixin} from '../mixins'
import * as Countries from '../constants/countries'
import * as ComplianceBundleStatuses from '../constants/compliance-bundle-statuses'

export default {
    mixins: [form_validation_mixin],
    props: {
        campaign: {
            required: true
        }
    },
    data() {
        return {
            loadingBtn: false,
            loading: false,
            dialogWhatIsVerifiedCallerId: false,
            pin: null,
            validationId: null,
            phoneNumber: '',
            extension: '',
            countryCode: '+1',
        }
    },
    computed: {
        sendButtonDisabled() {
            if (this.loading) return true
            return !this.isPhoneNumberValid
        },
        fixedPhone() {
            if (this.isPhoneNumberValid) {
                return this.$options.filters.fixPhone(this.countryCode + this.phoneNumber)
            }
            return null;
        },
        isPhoneNumberValid() {
            if (!this.phoneNumber || this.phoneNumber.trim() === '') return false
            const digitsOnly = this.phoneNumber.replace(/\D/g, '')
            return digitsOnly.length >= 10
        }
    },
    methods: {
        handleEnterKey(event) {
            event.preventDefault()
            event.stopPropagation()

            // Check if form is valid before submitting
            if (this.sendButtonDisabled) {
                return
            }

            this.submitForm()
        },
        handleButtonClick() {
            this.submitForm()
        },
        submitForm() {
            if (this.loading) return
            this.loading = true
            const payload = {
                phone_number: this.countryCode + this.phoneNumber,
                extension: this.extension || null
            }
            axios.post(`/api/v1/campaign/${this.campaign.id}/add-verified-caller-id`, payload).then(response => {
                if (response.data.message === 'Phone number is already verified.') {
                    this.$message({
                        message: 'Phone number is already verified',
                        type: 'warning'
                    })
                    this.loading = false
                    this.$emit('close')
                    this.$emit('moved')
                }

                if (response.data.message === 'Phone number added to line') {
                    this.$message({
                        message: 'Caller ID added successfully',
                        type: 'success'
                    })
                    this.loading = false
                    this.$emit('close')
                    this.$emit('moved')
                }

                if (response.data.validation_code) {
                    this.pin = response.data.validation_code
                    this.validationId = response.data.validation_id
                }
            }).catch(error => {
                this.$message({
                    message: error.response.data.error,
                    type: 'error'
                })
                this.loading = false
                console.error(error)
                this.$emit('close')
            })
        },
        closeIfLazy() {
            setTimeout(() => {
                if (this.loading) {
                    this.loading = false
                    this.$message({
                        message: 'Oops, it took too long to validate, please try again',
                        type: 'error'
                    })
                    this.$emit('close')
                }
            }, 100 * 1000)
        },
        getCountryPrefix(countryCode) {
            let country = Countries.COUNTRIES.find(item => {
                return item.code === countryCode
            })
            this.countryCode = country.prefix
        },
        changeCountry(countryCode) {
            this.getCountryPrefix(countryCode)
            this.phoneNumber = ''
            this.extension = ''
        },
    },
    beforeCreate() {
        this.pin = null
        this.loading = false
    },
    created() {
        VueEvent.listen('in_app_new_caller_id_return', (event_data) => {
            const validation = event_data.validation
            if (validation.id === this.validationId) {
                this.$emit('close')
                this.$emit('moved')
                switch (validation.status) {
                    case ComplianceBundleStatuses.STATUS_APPROVED:
                        this.$message({
                            message: 'Caller ID added successfully',
                            type: 'success'
                        })
                        break
                    case ComplianceBundleStatuses.STATUS_REJECTED:
                        this.$message({
                            message: validation.failure_reason,
                            type: 'error'
                        })
                        break
                }
            }
        })
    }
}
</script>
